import { Component, Output, EventEmitter, Input, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplicationDetailsService } from '../../loan-applications/loan-application-details/loan-application-details.service';
import { LoanApplicationService } from '../../loan-applications/loan-applications.service';
import { LoanApplicationUpdateDTO } from '../../loan-applications/loan-applications.interface';
import { UserKYCService } from '../../kyc/userkyc.service';
import { UserLoginService } from 'src/app/login/userlogin.service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { UserKYCDTO, UserKYCUpdateRequestDTO } from '../../kyc/userkyc.interface';
import { LosStateService } from '../los-state.service';
import { LoanApplicationItem } from '../../loan-applications/loan-applications.interface';
import { DocumentExtractionService } from 'src/app/Common/DocumentExtractionService/document-extraction.service';

@Component({
  selector: 'app-financial',
  templateUrl: './financial.component.html',
  styleUrls: ['./financial.component.css']
})
export class FinancialComponent implements OnInit {
  @Input() applicationId: number | null = null;
  @Output() next = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();

  private applicationDetailsService = inject(ApplicationDetailsService);
  private loanApplicationService    = inject(LoanApplicationService);
  private userKYCService            = inject(UserKYCService);
  private userLoginService          = inject(UserLoginService);
  private losState                  = inject(LosStateService);
  private toastr                    = inject(ToastrService);
  private destroyRef                = inject(DestroyRef);
  private documentExtractionService = inject(DocumentExtractionService);

  // ─── State ───────────────────────────────────────────────────────────────
  userKycData: UserKYCDTO | null      = null;
  applicationData: LoanApplicationItem | null = null;

  isExtractingDoc  = false;
  extractionError  = '';
  bankStatementData: any = null;
  showAllTransactions = false;

  occupation     = '';
  monthlyIncome  = 0;
  employerName   = '';
  yearsAtJob     = '';
  employmentType = '';

  accountNumber = '';
  ifscCode      = '';
  bankBranch    = '';
  accountHolder = '';

  selectedFileName = '';
  isSaving = false;

  readonly occupationOptions = ['Salaried', 'Self-employed', 'Business', 'Professional', 'Retired', 'Homemaker', 'Farmer'];
  readonly yearsOptions      = ['Less than 1 year', '1–2 years', '2–5 years', '5–10 years', '10+ years'];

  // ─── Lifecycle ───────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.losState.userKycData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.userKycData = data;
        if (data) {
          if (!this.occupation)     this.occupation     = data.occupationType  || '';
          if (!this.monthlyIncome)  this.monthlyIncome  = data.monthlyIncome   || 0;
          if (!this.employmentType) this.employmentType = data.employmentType  || '';
          if (!this.employerName)   this.employerName   = data.otherDetail1    || '';
        }
      });

    this.losState.applicationDetails$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(details => {
        if (!details?.length) return;
        this.patchFromDetails(details);
      });

    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => this.applicationData = app);
  }

  // ─── Patch saved details back into the form ───────────────────────────────
  private patchFromDetails(details: any[]): void {
    const g = (name: string) => details.find(d => d.parameterName === name)?.value ?? '';

    if (!this.occupation)     this.occupation     = g('Occupation');
    if (!this.monthlyIncome)  this.monthlyIncome  = +(g('Monthly Income'))  || 0;
    if (!this.employerName)   this.employerName   = g('Employer Name');
    if (!this.yearsAtJob)     this.yearsAtJob     = g('Years At Job');
    if (!this.employmentType) this.employmentType = g('Employment Type');
    if (!this.accountNumber)  this.accountNumber  = g('Bank Account Number');
    if (!this.ifscCode)       this.ifscCode       = g('Bank IFSC');
    if (!this.bankBranch)     this.bankBranch     = g('BS Bank Name');
    if (!this.accountHolder)  this.accountHolder  = g('BS Account Holder');

    // Restore full bank statement dashboard from persisted JSON if not already loaded
    if (!this.bankStatementData) {
      const raw = g('BS Analysis JSON');
      if (raw) {
        try { this.bankStatementData = JSON.parse(raw); } catch { /* ignore */ }
      }
    }
  }

  // ─── Computed ─────────────────────────────────────────────────────────────
  get isValid(): boolean {
    return !!this.occupation && this.monthlyIncome > 0;
  }

  private get actionUser(): string {
    return this.userLoginService.User()?.userId?.toString() || '0';
  }

  // ─── Bank lookup ─────────────────────────────────────────────────────────
  lookupBank(): void {
    if (!this.ifscCode) {
      this.toastr.warning('Enter IFSC code first');
      return;
    }
    this.toastr.info('Bank lookup — please verify details manually');
  }

  // ─── Document upload & extraction ────────────────────────────────────────
  onDocumentUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    this.selectedFileName = file.name;
    this.extractionError  = '';

    if (file.size > 10 * 1024 * 1024) {
      this.extractionError = 'File size exceeds the 10 MB limit. Please upload a smaller file.';
      this.toastr.error(this.extractionError);
      input.value = '';
      return;
    }

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      this.extractionError = 'Invalid file format. Please upload a PDF bank statement.';
      this.toastr.error(this.extractionError);
      input.value = '';
      return;
    }

    this.isExtractingDoc  = true;
    this.bankStatementData = null;

    this.documentExtractionService.extractDocument(file, 'BANK_STATEMENT', 'Y').subscribe({
      next: (res) => {
        this.isExtractingDoc = false;
        if (res?.data) {
          const data = res.data.data ?? res.data;
          this.bankStatementData = data;
          this.toastr.success('Bank statement analysed successfully!');
          this.autofillFromStatement(data);
        } else {
          this.extractionError = 'No data could be extracted from the uploaded bank statement.';
          this.toastr.warning(this.extractionError);
        }
        input.value = '';
      },
      error: (err) => {
        this.isExtractingDoc = false;
        this.extractionError = err.error?.message || err.message || 'An error occurred while analysing the document.';
        this.toastr.error(this.extractionError);
        input.value = '';
      }
    });
  }

  /** Auto-fill form fields from statement data */
  private autofillFromStatement(data: any): void {
    const id = data.bankStatementIdentity;
    if (id) {
      if (id.accountHolderName) this.accountHolder = id.accountHolderName;
      if (id.maskedAccountNumber) this.accountNumber = id.maskedAccountNumber;
      if (id.ifscCode)  this.ifscCode  = id.ifscCode;
      if (id.bankName)  this.bankBranch = id.bankName + (id.branchName ? ' – ' + id.branchName : '');
    }
    const avg = data.creditInflowAnalysis?.averageMonthlyCredit || 0;
    if (avg > 0 && (!this.monthlyIncome || this.monthlyIncome === 0)) {
      this.monthlyIncome = Math.round(avg);
    }
  }

  // ─── Save ─────────────────────────────────────────────────────────────────
  async saveFinancialDetails(): Promise<void> {
    if (!this.isValid) {
      this.toastr.warning('Please fill occupation and monthly income');
      return;
    }
    if (this.isSaving) return;
    this.isSaving = true;

    try {
      const fields = this.buildApplicationDetailFields();
      for (const field of fields) {
        if (!field.value) continue;
        await firstValueFrom(
          this.applicationDetailsService.ApplicationDetails_UpdateByParameterName({
            applicationId:      this.applicationId!,
            parameterName:      field.name,
            value:              field.value,
            isVerified:         field.verified ?? 0,
            verificationSource: field.source   ?? 'Customer_Entered',
            actionUser:         this.actionUser
          })
        );
      }

      if (this.applicationId) {
        this.losState.refreshDetails(this.applicationId);
      }

      // Update UserKYC table
      await this.saveKycTable();

      // Update application status
      if (this.applicationData && this.applicationId) {
        const payload = this.buildAppUpdatePayload('FinancialComplete');
        const updated = await firstValueFrom(this.loanApplicationService.LoanApplication_Update(payload));
        this.losState.patchApplication(updated);
      }

      this.toastr.success('Financial details saved successfully');
      this.isSaving = false;
      this.next.emit();
    } catch (err) {
      this.toastr.error('Failed to save financial details');
      console.error('[Financial] save error:', err);
      this.isSaving = false;
    }
  }

  // ─── Build ApplicationDetails field list ─────────────────────────────────
  /**
   * Every field stored here is readable by Bureau+AA and any downstream step.
   * Naming convention:
   *   Plain fields  → 'Occupation', 'Monthly Income', …
   *   Bank stmt     → 'BS <FieldName>'   (prefix keeps them grouped)
   */
  private buildApplicationDetailFields(): Array<{ name: string; value: string; verified?: number; source?: string }> {
    const bs = this.bankStatementData;

    const fields: Array<{ name: string; value: string; verified?: number; source?: string }> = [
      // ── Employment / income ─────────────────────────────────────────────
      { name: 'Occupation',          value: this.occupation,           verified: 0, source: 'Customer_Entered' },
      { name: 'Monthly Income',      value: String(this.monthlyIncome),verified: 0, source: 'Customer_Entered' },
      { name: 'Employer Name',       value: this.employerName,         verified: 0, source: 'Customer_Entered' },
      { name: 'Years At Job',        value: this.yearsAtJob,           verified: 0, source: 'Customer_Entered' },
      { name: 'Employment Type',     value: this.employmentType,       verified: 0, source: 'Customer_Entered' },
      // ── Bank account ─────────────────────────────────────────────────────
      { name: 'Bank Account Number', value: this.accountNumber,        verified: 0, source: 'Customer_Entered' },
      { name: 'Bank IFSC',           value: this.ifscCode,             verified: 0, source: 'Customer_Entered' },
    ];

    if (!bs) return fields;

    // ── Bank Statement Identity ──────────────────────────────────────────────
    const id = bs.bankStatementIdentity || {};
    fields.push(
      { name: 'BS Bank Name',             value: id.bankName                   || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Account Holder',        value: id.accountHolderName          || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Masked Account Number', value: id.maskedAccountNumber        || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS IFSC Code',             value: id.ifscCode                   || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS MICR Code',             value: id.micrCode                   || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Account Type',          value: id.accountType                || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Branch Name',           value: id.branchName                 || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Branch Address',        value: id.branchAddress              || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Currency',              value: id.currency                   || 'INR', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Statement Start Date',  value: id.statementStartDate         || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Statement End Date',    value: id.statementEndDate           || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Statement Generated',   value: id.statementGeneratedDate     || '', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Statement Period Months', value: String(id.statementPeriodMonths || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Missing Pages',         value: id.missingPagesDetected ? '1' : '0', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Duplicate Detected',    value: id.duplicateStatementDetected ? '1' : '0', verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── File Authenticity ────────────────────────────────────────────────────
    const fa = bs.fileAuth || {};
    fields.push(
      { name: 'BS File Auth Score',       value: String(fa.fileAuthenticityScore   || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Page Count',            value: String(fa.pageCount               || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Password Protected',    value: fa.isPasswordProtected ? '1' : '0',      verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Tampering Suspected',   value: fa.metadataTamperingSuspected ? '1' : '0', verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── Transaction Authenticity ──────────────────────────────────────────────
    const ta = bs.transactionAuthenticity || {};
    fields.push(
      { name: 'BS Txn Auth Score',            value: String(ta.transactionAuthenticityScore || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Tampering Flagged',         value: ta.tamperingFlagged ? '1' : '0',              verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Balance Continuity Fails',  value: String(ta.balanceContinuityFailureCount || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Duplicate Txn Count',       value: String(ta.duplicateTransactionCount    || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Total Transactions',        value: String(ta.totalTransactions             || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Closing Balance Discrepancy', value: String(ta.closingBalanceDiscrepancy  || 0), verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── Credit / Inflow ───────────────────────────────────────────────────────
    const ci = bs.creditInflowAnalysis || {};
    fields.push(
      { name: 'BS Total Credit',              value: String(ci.totalCreditAmount           || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Avg Monthly Credit',        value: String(ci.averageMonthlyCredit        || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Median Monthly Credit',     value: String(ci.medianMonthlyCredit         || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS UPI Inflow Amount',         value: String(ci.upiInflowAmount             || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Cash Deposit Amount',       value: String(ci.cashDepositAmount           || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Salary Credit Amount',      value: String(ci.salaryCreditAmount          || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Business Receipt Amount',   value: String(ci.businessReceiptAmount       || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Family Transfer Amount',    value: String(ci.familyTransferAmount        || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Unknown Large Credit Count', value: String(ci.unknownLargeCreditCount    || 0), verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── Debit / Outflow ───────────────────────────────────────────────────────
    const di = bs.debitOutflowAnalysis || {};
    fields.push(
      { name: 'BS Total Debit',               value: String(di.totalDebitAmount            || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Avg Monthly Debit',         value: String(di.averageMonthlyDebit         || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS UPI Outflow Amount',        value: String(di.upiOutflowAmount            || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS ATM Withdrawal Amount',     value: String(di.atmWithdrawalAmount         || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Wallet Outflow',            value: String(di.walletOutflow               || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Shopping Outflow',          value: String(di.shoppingOutflow             || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Utility Outflow',           value: String(di.utilityOutflow              || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Rent Outflow',              value: String(di.rentOutflow                 || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS EMI Outflow',               value: String(di.emiOutflow                  || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS P2P Transfer Outflow',      value: String(di.p2pTransferOutflow          || 0), verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── Salary / Income Trust ─────────────────────────────────────────────────
    const sit = bs.salaryIncomeTrust || {};
    fields.push(
      { name: 'BS Salary Detected',           value: sit.salaryDetected ? '1' : '0',              verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Avg Monthly Salary',        value: String(sit.averageMonthlySalary      || 0),  verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Income Trust Score',        value: String(sit.incomeTrustScore          || 0),  verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Income Volatility Pct',     value: String(sit.incomeVolatilityPercentage || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Salary Stopped Recently',   value: sit.salaryStoppedRecently ? '1' : '0',       verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Income Drop 30Pct',         value: sit.incomeDropMoreThan30Percent ? '1' : '0', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Salary Date Variation Days', value: String(sit.salaryDateVariationDays  || 0),  verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── Balance Health ────────────────────────────────────────────────────────
    const bh = bs.balanceHealth || {};
    fields.push(
      { name: 'BS Avg Monthly Balance',       value: String(bh.averageMonthlyBalance      || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Median Monthly Balance',    value: String(bh.medianMonthlyBalance       || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Minimum Balance',           value: String(bh.minimumBalance             || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Maximum Balance',           value: String(bh.maximumBalance             || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Low Balance Days',          value: String(bh.lowBalanceDays             || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Negative Balance Days',     value: String(bh.negativeBalanceDays        || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Min Balance Penalty Count', value: String(bh.minimumBalancePenaltyCount || 0), verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── EMI Liability ─────────────────────────────────────────────────────────
    const eli = bs.emiLiabilityAnalysis || {};
    fields.push(
      { name: 'BS Existing EMI Count',        value: String(eli.existingEmiCount             || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Monthly EMI Amount',        value: String(eli.existingMonthlyEmiAmount     || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS EMI Bounce Count',          value: String(eli.emiBounceCount               || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Cheque Bounce Count',       value: String(eli.chequeBounceCount            || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS NACH Return Count',         value: String(eli.nachReturnCount              || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Auto Debit Failure Count',  value: String(eli.autoDebitFailureCount        || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Hidden Liability Suspected', value: eli.hiddenLiabilitySuspected ? '1' : '0',     verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Liability Stress Score',    value: String(eli.liabilityStressScore         || 0), verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── Expense Stability ─────────────────────────────────────────────────────
    const es = bs.expenseStability || {};
    fields.push(
      { name: 'BS Fixed Expense Amount',      value: String(es.fixedExpenseAmount            || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Variable Expense Amount',   value: String(es.variableExpenseAmount         || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Expense Volatility Pct',    value: String(es.expenseVolatilityPercentage   || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Expense Stability Score',   value: String(es.expenseStabilityScore         || 0), verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── UPI Flow ──────────────────────────────────────────────────────────────
    const upi = bs.upiFlowAnalysis || {};
    fields.push(
      { name: 'BS UPI Inflow Total',          value: String(upi.totalUpiInflow              || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS UPI Outflow Total',         value: String(upi.totalUpiOutflow             || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS UPI Inflow Count',          value: String(upi.upiInflowCount              || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS UPI Outflow Count',         value: String(upi.upiOutflowCount             || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS UPI Net Flow',              value: String(upi.upiNetFlow                  || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Paytm Topup Count',         value: String(upi.paytmWalletTopupCount       || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Paytm Topup Amount',        value: String(upi.paytmWalletTopupAmount      || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS UPI Circular Suspected',    value: upi.upiCircularFlowSuspected ? '1' : '0',     verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── Cash Flow ─────────────────────────────────────────────────────────────
    const cf = bs.cashFlowAnalysis || {};
    fields.push(
      { name: 'BS Cash Withdrawal Amount',    value: String(cf.cashWithdrawalAmount         || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Cash Withdrawal Count',     value: String(cf.cashWithdrawalCount          || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Cash Heavy Profile',        value: cf.cashHeavyProfile ? '1' : '0',              verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Cash Withdrawal Ratio Pct', value: String(cf.cashWithdrawalRatioAgainstDebit || 0), verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── Surplus / Repayment ───────────────────────────────────────────────────
    const sr = bs.surplusRepayment || {};
    fields.push(
      { name: 'BS Monthly Surplus',           value: String(sr.monthlySurplusAmount         || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Savings Rate Pct',          value: String(sr.savingsRatePercentage        || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Income Expense Ratio',      value: String(sr.incomeToExpenseRatio         || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Repayment Capacity Score',  value: String(sr.repaymentCapacityScore       || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Cashflow Stability Score',  value: String(sr.cashflowStabilityScore       || 0), verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── Risk / Fraud Signals ──────────────────────────────────────────────────
    const rf = bs.riskFraudSignals || {};
    fields.push(
      { name: 'BS Fraud Risk Score',              value: String(rf.fraudRiskScore              || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Gambling Txn Count',            value: String(rf.gamblingTransactionCount    || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Crypto Txn Count',              value: String(rf.cryptoTransactionCount      || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Suspicious Dummy Name',         value: rf.suspiciousDummyNameTransfer ? '1' : '0', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Circular Txn Detected',         value: rf.circularTransactionDetected ? '1' : '0', verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Frequent Loan App Txns',        value: String(rf.frequentLoanAppTransactions || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Artificial Balance Buildup',    value: rf.artificialBalanceBuildUpSuspected ? '1' : '0', verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── FOIR ─────────────────────────────────────────────────────────────────
    const foir = bs.foirAnalysis || {};
    fields.push(
      { name: 'BS FOIR Percentage',           value: String(foir.foirPercentage             || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS FOIR Risk Level',           value: foir.foirRiskLevel                     || '',  verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Net Monthly Income',        value: String(foir.netMonthlyIncome           || 0), verified: 1, source: 'Bank_Statement_AI' },
      { name: 'BS Total Monthly Obligation',  value: String(foir.totalMonthlyObligation     || 0), verified: 1, source: 'Bank_Statement_AI' },
    );

    // ── Full JSON for restoring dashboard without re-upload ───────────────────
    fields.push({
      name:     'BS Analysis JSON',
      value:    JSON.stringify(bs),
      verified: 1,
      source:   'Bank_Statement_AI'
    });

    return fields;
  }

  // ─── Update UserKYC table with income data ─────────────────────────────────
  private async saveKycTable(): Promise<void> {
    if (!this.userKycData) return;
    const kycPayload: UserKYCUpdateRequestDTO = {
      kycId:            this.userKycData.kycId,
      userId:           this.userKycData.userId,
      careOf:           this.userKycData.careOf           || '',
      relation:         this.userKycData.relation         || '',
      fullAddress:      this.userKycData.fullAddress      || '',
      dateOfBirth:      this.userKycData.dateOfBirth      || null,
      yearOfBirth:      this.userKycData.yearOfBirth      || 0,
      email:            this.userKycData.email            || '',
      mobile:           this.userKycData.mobile           || '',
      gender:           this.userKycData.gender           || '',
      name:             this.userKycData.name             || '',
      aadharNumber:     this.userKycData.aadharNumber     || '',
      panNumber:        this.userKycData.panNumber        || '',
      photo:            this.userKycData.photo            || '',
      shareCode:        this.userKycData.shareCode        || '',
      country:          this.userKycData.country          || '',
      state:            this.userKycData.state            || '',
      district:         this.userKycData.district         || '',
      subDistrict:      this.userKycData.subDistrict      || '',
      vtc:              this.userKycData.vtc              || '',
      street:           this.userKycData.street           || '',
      house:            this.userKycData.house            || '',
      landmark:         this.userKycData.landmark         || '',
      postOffice:       this.userKycData.postOffice       || '',
      pincode:          this.userKycData.pincode          || '',
      isAadharVerified: this.userKycData.isAadharVerified || 0,
      isPanVerified:    this.userKycData.isPanVerified    || 0,
      monthlyIncome:    this.monthlyIncome,
      occupationType:   this.occupation,
      employmentType:   this.employmentType,
      aadhaarXML:       this.userKycData.aadhaarXML       || '',
      panXML:           this.userKycData.panXML           || '',
      panName:          this.userKycData.panName          || '',
      panAddress:       this.userKycData.panAddress       || '',
      panGender:        this.userKycData.panGender        || '',
      aadhaarLinked:    this.userKycData.aadhaarLinked    || 0,
      panDob:           this.userKycData.panDob           || null,
      otherDetail1:     this.employerName,
      otherDetail2:     this.userKycData.otherDetail2     || '',
      otherDetail3:     this.userKycData.otherDetail3     || '',
      otherDetail4:     this.userKycData.otherDetail4     || '',
      otherDetail5:     this.userKycData.otherDetail5     || '',
      otherDetail6:     this.userKycData.otherDetail6     || '',
      isActive:         1,
      actionUser:       this.actionUser
    };
    await firstValueFrom(this.userKYCService.UserKYC_Update(kycPayload));
    this.losState.refreshKyc(this.userKycData.userId);
  }

  // ─── Build loan application update payload ─────────────────────────────────
  private buildAppUpdatePayload(status: string): LoanApplicationUpdateDTO {
    const a = this.applicationData!;
    return {
      applicationId:      a.applicationId,
      userId:             a.userId,
      productId:          a.productId,
      applicationStatus:  status,
      requestedAmount:    a.requestedAmount,
      approvedAmount:     a.approvedAmount,
      tenureMonths:       a.tenureMonths,
      applicationDate:    a.applicationDate,
      approvalDate:       a.approvalDate,
      approvedBy:         a.approvedBy         || '',
      rejectionReason:    a.rejectionReason    || '',
      rejectedBy:         a.rejectedBy         || '',
      isDisbursed:        a.isDisbursed,
      disbursedOn:        a.disbursedOn,
      disbursedAmount:    a.disbursedAmount,
      subUserId:          a.subUserId,
      businessId:         a.businessId,
      productCode:        a.productCode        || '',
      prrn:               a.prrn,
      lmsApplicationName: a.lmsApplicationName,
      otherDetail1:       a.otherDetail1,
      otherDetail2:       a.otherDetail2,
      otherDetail3:       a.otherDetail3,
      otherDetail4:       a.otherDetail4,
      otherDetail5:       a.otherDetail5,
      otherDetail6:       a.otherDetail6,
      isActive:           1,
      actionUser:         this.actionUser
    };
  }
}